import { useContext, useId, useRef, useState } from 'react';

import { IconLogout, IconSettings } from '@tabler/icons-react';
import { ListItemIcon, Tooltip } from '@mui/material';

import { signOut } from '../../../services/auth';
import { getProfileIconParts, useGetCurrentUserQuery } from '../../../services/users';
import { SIGN_IN_HREF } from '../../../utils/common/hrefs';
import reloadTo from '../../../utils/common/reloadTo';
import { allowUnload, hasUnsavedChanges } from '../../../utils/react/hooks/useRefreshWarning';
import useSnackBar from '../../../utils/react/hooks/useSnackbar';
import ListItemButtonLink from '../../ListItemButtonLink';
import Modal from '../../Modal';
import ProfileIcon from '../../ProfileIcon';
import { SettingsOpenContext } from '../../SettingsModal/context';
import { SkeletonCircle, SkeletonGroup, SkeletonText } from '../../Skeleton';
import { ListItemText } from '../MenuList/index.styles';
import { SETTINGS_LANDING_HREF } from '../settings';

import { joinNames } from '@repo/utils/common';
import {
  Identity,
  IdentityMessage,
  IdentityText,
  MenuDivider,
  Name,
  ProfileList,
  ProfileListItem,
  StyledMenu,
} from './index.styles';

/** Photo diameter in the menu, sized to the nav's icon column. */
const PROFILE_ICON_SIZE = 40;

/** Aligns the menu's left edge with the nav rows rather than the window inset. */
const MENU_MARGIN = 8;

interface ProfileMenuProps {
  /** Matches the drawer state, so the label clips with the nav items. */
  expanded?: boolean;
  /** Fires before navigation, so the mobile sheet closes behind the menu. */
  onNavigate?: () => void;
}

/**
 * The signed-in user, as the last entry of the navigation. Opens a menu rather
 * than a page: everything it holds is either read-only or a way out.
 */
const ProfileMenu = ({ expanded = true, onNavigate }: ProfileMenuProps) => {
  const isSettingsOpen = useContext(SettingsOpenContext);
  const createSnackbar = useSnackBar();
  const { data: currUser, isLoading, error } = useGetCurrentUserQuery();

  const menuId = useId();
  const triggerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmingSignOut, setIsConfirmingSignOut] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  // Read synchronously: two presses in one tick both see the state unchanged
  const isLeaving = useRef(false);

  const user = currUser?.data;
  const { color: profileColor, image: profileImage } = getProfileIconParts(user?.profileIcon);
  const name = joinNames(user?.firstName, user?.lastName);

  const closeMenu = () => setIsOpen(false);

  const leaveMenu = () => {
    closeMenu();
    onNavigate?.();
  };

  const leaveSession = async () => {
    // Cognito takes a round trip during which nothing on screen moves, so a
    // second press would otherwise sign out again and leave twice
    if (isLeaving.current) {
      return;
    }
    isLeaving.current = true;
    setIsSigningOut(true);

    try {
      await signOut();
    } catch {
      // Nothing has been cleared, so the session the reader still holds stands
      isLeaving.current = false;
      setIsSigningOut(false);
      createSnackbar('Could not sign out. Please try again.', 'error');
      return;
    }

    leaveMenu();
    // The reader has agreed to lose the page, so it goes without a second ask
    allowUnload();
    reloadTo(SIGN_IN_HREF);
  };

  const handleSignOut = async () => {
    // Asked here because the session is already gone by the time the browser
    // would ask, leaving a page that cannot save what it is still holding
    if (hasUnsavedChanges()) {
      closeMenu();
      setIsConfirmingSignOut(true);
      return;
    }

    await leaveSession();
  };

  const triggerLabel = user?.firstName ?? 'Profile';

  const renderIdentity = () => {
    if (isLoading) {
      return (
        <SkeletonGroup label='Loading your details'>
          <Identity>
            <SkeletonCircle size={PROFILE_ICON_SIZE} />
            <IdentityText>
              <SkeletonText variant='body1' width={120} />
            </IdentityText>
          </Identity>
        </SkeletonGroup>
      );
    }

    if (error && !user) {
      return <IdentityMessage variant='body2'>Your details could not be loaded.</IdentityMessage>;
    }

    return (
      <Identity>
        <ProfileIcon
          firstName={user?.firstName}
          lastName={user?.lastName}
          color={profileColor}
          imageUrl={profileImage}
          size={PROFILE_ICON_SIZE}
        />
        <IdentityText>
          <Name variant='body1'>{name}</Name>
        </IdentityText>
      </Identity>
    );
  };

  return (
    <>
      <ProfileList>
        <Tooltip title={expanded ? '' : triggerLabel} placement='right'>
          <ProfileListItem
            ref={triggerRef}
            onClick={() => setIsOpen(true)}
            // Settings live behind this menu, so it carries the selection there
            selected={isOpen || isSettingsOpen}
            aria-haspopup='menu'
            aria-expanded={isOpen}
            aria-controls={isOpen ? menuId : undefined}
            aria-label='Account menu'
            disableRipple
          >
            <ListItemIcon>
              <ProfileIcon
                firstName={user?.firstName}
                lastName={user?.lastName}
                color={profileColor}
                imageUrl={profileImage}
                size={24}
              />
            </ListItemIcon>
            <ListItemText
              primary={triggerLabel}
              primaryTypographyProps={{ variant: 'body2' }}
              $expanded={expanded}
            />
          </ProfileListItem>
        </Tooltip>
      </ProfileList>

      <StyledMenu
        id={menuId}
        anchorEl={triggerRef.current}
        open={isOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        marginThreshold={MENU_MARGIN}
        // The identity leads the menu but is nothing to arrow onto, so the
        // container takes the focus and the arrow keys reach the rows from it
        variant='menu'
        disableAutoFocusItem
        // A div rather than the default list: its children are the identity
        // block and a divider as well as the two rows
        slotProps={{ list: { 'aria-label': 'Account', component: 'div' } }}
      >
        {renderIdentity()}
        <MenuDivider />
        <ListItemButtonLink
          role='menuitem'
          href={SETTINGS_LANDING_HREF}
          onClick={leaveMenu}
          selected={isSettingsOpen}
          disableRipple
        >
          <ListItemIcon>
            <IconSettings />
          </ListItemIcon>
          <ListItemText
            primary='Settings'
            primaryTypographyProps={{ variant: 'body2' }}
            $expanded
          />
        </ListItemButtonLink>
        <ProfileListItem
          role='menuitem'
          onClick={handleSignOut}
          disabled={isSigningOut}
          disableRipple
        >
          <ListItemIcon>
            <IconLogout />
          </ListItemIcon>
          <ListItemText
            primary='Sign out'
            primaryTypographyProps={{ variant: 'body2' }}
            $expanded
          />
        </ProfileListItem>
      </StyledMenu>

      <Modal
        open={isConfirmingSignOut}
        // Sealed once the sign-out is away: dismissing it then would read as
        // cancelling something that cannot be called back
        handleClose={() => !isSigningOut && setIsConfirmingSignOut(false)}
        hideCloseIcon={isSigningOut}
        title='Are you sure?'
        description='Signing out will discard work that has not finished saving.'
        buttons={[
          {
            label: 'Cancel',
            closeOnClick: true,
            disabled: isSigningOut,
          },
          {
            label: 'Sign out',
            onClick: leaveSession,
            loading: isSigningOut,
            color: 'error',
          },
        ]}
      />
    </>
  );
};

export default ProfileMenu;
